import type { Property } from '../../components/portfolioMetrics';
import type { SellListing } from '../shared/sellListings';

// ============================================================
// Sample data for the login-free Asset Desk tour (AssetDeskDemo).
//
// WHY THIS FILE EXISTS: a prospect who has not bought a unit yet has
// nothing to look at — every screen is driven by `units where
// investorId == <account>`, so an account of their own shows an empty
// Asset Desk. This gives the tour a full portfolio to render.
//
// WHAT SHAPE THIS MUST BE IN: the tour renders the REAL Asset Desk
// screens (PortfolioView ▸ Valuation ▸ UnitValuationReport), not a
// mockup. So the data here has to match what those components read:
//   • DEMO_PROPERTIES  — the `Property` shape PortfolioView lists
//   • DEMO_DLD_DATA    — the /dld/market-intelligence response shape,
//                        handed to PortfolioView as `dldDataOverride`
// Add a field the real report reads and forget it here, and the report
// silently renders an em-dash. Cross-check against the `dldUnit?.…`
// reads in UnitValuationReport when editing.
//
// The portfolio is ten units — 5 rented, 2 sold, 3 under construction —
// built for the product films; see THE SAMPLE PORTFOLIO below.
//
// SAFETY: these payloads are hardcoded on purpose. PortfolioView,
// PortfolioReport, PortfolioRevenue and UnitValuationReport all guard
// their fetches on investorId/apiUrl, and the tour passes neither — so
// a tour that needs no login makes no network call and cannot reach a
// real investor's data.
//
// The units are FICTIONAL (invented unit numbers and purchase
// prices) in REAL projects, so figures stay plausible to a Dubai
// investor. They are a snapshot, not a live valuation — the tour says
// so on every screen.
//
// Keep the arithmetic consistent when editing: pricePerSqft × size =
// market value, rent ÷ value = yield, comp prices bracket the estimate.
// The report prints a "how we calculated this" block showing the
// division, so a prospect who checks it must not catch us out.
// ============================================================

const SNAPSHOT_AT = '2026-08-01T00:00:00.000Z';

/** Snapshot date shown in the tour's sample-data banner. */
export const DEMO_SNAPSHOT_AT = SNAPSHOT_AT;

// ------------------------------------------------------------
// Unit 1 — Seagate, Mina Rashid. Handed over and tenanted: the strong
// case, with same-project comparables and real Ejari spread.
// ------------------------------------------------------------
const SEAGATE_DLD = {
  unitId: 'demo-unit-seagate',
  unitName: 'Seagate Building 2 · 1204',
  project: 'Seagate',
  area: 'Mina Rashid',

  dldMarketValue: 3690000,
  dldMarketValueLow: 3520000,
  dldMarketValueHigh: 3860000,
  dldPricePerSqft: 2613,
  valuationConfidence: 'high',
  estimateConfidence: 'high',
  comparablesCount: 4,
  valuationCompsUsed: 4,
  strictCompsCount: 4,
  valuationDedupDropped: 2,
  valuationWindowMonths: 12,
  roiReliable: true,
  matchType: 'project',
  matchArea: 'Mina Rashid',
  marketValuation: { source_label: 'Seagate · 2 B/R · median AED/sqft, last 12 months' },

  purchaseValue: 3150000,
  currentValue: 3690000,
  unrealizedGain: 540000,
  marketAppreciation: 17.1,

  dldAvgAnnualRent: 195000,
  dldAvgMonthlyRent: 16250,
  dldRentalYield: 5.28,
  rentalComparablesCount: 4,
  rentalMatchType: 'project',

  // Monthly median AED/sqft — drives the trend chart (needs ≥ 2 points).
  priceTrend: {
    change6m: 4.8,
    series: [
      { month: '2026-02', pricePerSqft: 2494 },
      { month: '2026-03', pricePerSqft: 2521 },
      { month: '2026-04', pricePerSqft: 2558 },
      { month: '2026-05', pricePerSqft: 2564 },
      { month: '2026-06', pricePerSqft: 2601 },
      { month: '2026-07', pricePerSqft: 2613 },
    ],
  },

  // The exact sales behind the number — the report's primary evidence
  // table. sizeDeltaPct is each comp's size against this unit's 1,412 sqft.
  estimateComparables: [
    { transactionId: 'DLD-2026-4418207', date: '2026-06-18', project: 'Seagate', rooms: '2 B/R', sizeSqft: 1405, amount: 3720000, pricePerSqft: 2648, isOffplan: false, sizeDeltaPct: -0.5 },
    { transactionId: 'DLD-2026-4302915', date: '2026-05-27', project: 'Seagate', rooms: '2 B/R', sizeSqft: 1398, amount: 3585000, pricePerSqft: 2564, isOffplan: false, sizeDeltaPct: -1.0 },
    { transactionId: 'DLD-2026-4166432', date: '2026-04-09', project: 'Seagate', rooms: '2 B/R', sizeSqft: 1442, amount: 3810000, pricePerSqft: 2642, isOffplan: false, sizeDeltaPct: 2.1 },
    { transactionId: 'DLD-2026-4051188', date: '2026-03-21', project: 'Seagate', rooms: '2 B/R', sizeSqft: 1412, amount: 3640000, pricePerSqft: 2578, isOffplan: false, sizeDeltaPct: 0 },
  ],
  // No separate same-building context pool in the sample — the estimate
  // evidence above is the whole story, which keeps the tour honest.
  buildingSales: [],
  recentComparables: [
    { date: '2026-06-18', amount: 3720000, sizeSqft: 1405, pricePerSqft: 2648, rooms: '2 B/R', type: 'Apartment', isOffplan: 'No', project: 'Seagate' },
    { date: '2026-05-27', amount: 3585000, sizeSqft: 1398, pricePerSqft: 2564, rooms: '2 B/R', type: 'Apartment', isOffplan: 'No', project: 'Seagate' },
    { date: '2026-04-09', amount: 3810000, sizeSqft: 1442, pricePerSqft: 2642, rooms: '2 B/R', type: 'Apartment', isOffplan: 'No', project: 'Seagate' },
    { date: '2026-03-21', amount: 3640000, sizeSqft: 1412, pricePerSqft: 2578, rooms: '2 B/R', type: 'Apartment', isOffplan: 'No', project: 'Seagate' },
  ],
  recentRentals: [
    { startDate: '2026-06-01', annualRent: 198000, monthlyRent: 16500, sizeSqft: 1405, rooms: '2 B/R', isRenewal: false, project: 'Seagate' },
    { startDate: '2026-04-15', annualRent: 185000, monthlyRent: 15417, sizeSqft: 1398, rooms: '2 B/R', isRenewal: true, project: 'Seagate' },
    { startDate: '2026-02-10', annualRent: 205000, monthlyRent: 17083, sizeSqft: 1442, rooms: '2 B/R', isRenewal: false, project: 'Seagate' },
    { startDate: '2026-01-05', annualRent: 192000, monthlyRent: 16000, sizeSqft: 1412, rooms: '2 B/R', isRenewal: true, project: 'Seagate' },
  ],
  lastSync: SNAPSHOT_AT,
};

// ------------------------------------------------------------
// Unit 2 — Damac Bay 2, Dubai Harbour. Off-plan and still building:
// off-plan comparables, and rent evidence that has to come from the
// surrounding area because the tower has no Ejari history yet. Showing
// that fallback is the point — it is how the product behaves.
// ------------------------------------------------------------
const DAMAC_BAY_DLD = {
  unitId: 'demo-unit-damac-bay',
  unitName: 'Damac Bay 2 · Tower B · 2408',
  project: 'Damac Bay 2',
  area: 'Dubai Harbour',

  dldMarketValue: 2340000,
  dldMarketValueLow: 2220000,
  dldMarketValueHigh: 2455000,
  dldPricePerSqft: 2992,
  valuationConfidence: 'medium',
  estimateConfidence: 'medium',
  comparablesCount: 4,
  valuationCompsUsed: 4,
  strictCompsCount: 4,
  valuationDedupDropped: 0,
  valuationWindowMonths: 12,
  roiReliable: false,
  matchType: 'project',
  matchArea: 'Dubai Harbour',
  marketValuation: { source_label: 'Damac Bay 2 · 1 B/R off-plan · median AED/sqft, last 12 months' },

  purchaseValue: 2050000,
  currentValue: 2340000,
  unrealizedGain: 290000,
  marketAppreciation: 14.1,

  dldAvgAnnualRent: 145000,
  dldAvgMonthlyRent: 12083,
  dldRentalYield: 6.2,
  rentalComparablesCount: 4,
  rentalMatchType: 'area',

  priceTrend: {
    change6m: 3.1,
    series: [
      { month: '2026-02', pricePerSqft: 2887 },
      { month: '2026-03', pricePerSqft: 2942 },
      { month: '2026-04', pricePerSqft: 2955 },
      { month: '2026-05', pricePerSqft: 3028 },
      { month: '2026-06', pricePerSqft: 2946 },
      { month: '2026-07', pricePerSqft: 2992 },
    ],
  },

  // sizeDeltaPct against this unit's 782 sqft.
  estimateComparables: [
    { transactionId: 'DLD-2026-4502771', date: '2026-07-02', project: 'Damac Bay 2', rooms: '1 B/R', sizeSqft: 788, amount: 2395000, pricePerSqft: 3039, isOffplan: true, sizeDeltaPct: 0.8 },
    { transactionId: 'DLD-2026-4389604', date: '2026-06-11', project: 'Damac Bay 2', rooms: '1 B/R', sizeSqft: 774, amount: 2280000, pricePerSqft: 2946, isOffplan: true, sizeDeltaPct: -1.0 },
    { transactionId: 'DLD-2026-4231459', date: '2026-05-05', project: 'Damac Bay 2', rooms: '1 B/R', sizeSqft: 796, amount: 2410000, pricePerSqft: 3028, isOffplan: true, sizeDeltaPct: 1.8 },
    { transactionId: 'DLD-2026-4098330', date: '2026-03-30', project: 'Damac Bay 2', rooms: '1 B/R', sizeSqft: 770, amount: 2265000, pricePerSqft: 2942, isOffplan: true, sizeDeltaPct: -1.5 },
  ],
  buildingSales: [],
  recentComparables: [
    { date: '2026-07-02', amount: 2395000, sizeSqft: 788, pricePerSqft: 3039, rooms: '1 B/R', type: 'Apartment', isOffplan: 'Yes', project: 'Damac Bay 2' },
    { date: '2026-06-11', amount: 2280000, sizeSqft: 774, pricePerSqft: 2946, rooms: '1 B/R', type: 'Apartment', isOffplan: 'Yes', project: 'Damac Bay 2' },
    { date: '2026-05-05', amount: 2410000, sizeSqft: 796, pricePerSqft: 3028, rooms: '1 B/R', type: 'Apartment', isOffplan: 'Yes', project: 'Damac Bay 2' },
    { date: '2026-03-30', amount: 2265000, sizeSqft: 770, pricePerSqft: 2942, rooms: '1 B/R', type: 'Apartment', isOffplan: 'Yes', project: 'Damac Bay 2' },
  ],
  // Area-tier Ejari: the tower is unbuilt, so these are 1 B/R contracts
  // from Dubai Harbour rather than from the building itself.
  recentRentals: [
    { startDate: '2026-06-20', annualRent: 150000, monthlyRent: 12500, sizeSqft: 788, rooms: '1 B/R', isRenewal: false, project: 'Dubai Harbour' },
    { startDate: '2026-05-02', annualRent: 138000, monthlyRent: 11500, sizeSqft: 774, rooms: '1 B/R', isRenewal: true, project: 'Dubai Harbour' },
    { startDate: '2026-03-12', annualRent: 155000, monthlyRent: 12917, sizeSqft: 796, rooms: '1 B/R', isRenewal: false, project: 'Dubai Harbour' },
    { startDate: '2026-01-22', annualRent: 142000, monthlyRent: 11833, sizeSqft: 770, rooms: '1 B/R', isRenewal: true, project: 'Dubai Harbour' },
  ],
  lastSync: SNAPSHOT_AT,
};

// ============================================================
// THE SAMPLE PORTFOLIO — ten units.
//
// Composition is the one asked for for the product films:
//   5 handed over and rented · 2 sold at a profit · 3 under construction
// which is also a fair picture of a real Dubai portfolio: income today,
// realised returns behind it, and capital still being built out.
//
// Every `project` here matches a key in FALLBACK_PROJECT_IMAGES
// (PropertyView / MainDashboard), so each card renders with its project
// photo instead of the gold placeholder — the difference between a
// portfolio that films well and one that does not. Check that map before
// renaming a project.
//
// FICTIONAL units and prices in REAL projects: plausible to a Dubai
// investor, and not anybody's actual holding.
//
// Statuses are what the tabs filter on, and they are not interchangeable:
//   PropertyView ▸ Rented  reads `status === 'rented'`
//   PropertyView ▸ Sold    reads `status === 'sold'` (isSold in portfolioMetrics)
//   Active                 is everything not sold
// `occupancyStatus` only drives the badge on the card.
// ============================================================

/**
 * Handover for the near-complete unit: about a month out, computed rather
 * than written down so it never drifts into the past between shoots.
 */
export const HANDOVER_SOON = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 34);
  return d.toISOString().slice(0, 10);
})();

/** Same investor id as DEMO_INVESTOR — screens filter units by it. */
const DEMO_INVESTOR_ID = 'sample.investor@example.com';

// --- The five that are handed over and tenanted -------------------------
// Rents are monthly, and each one is backed by the Ejari evidence built for
// it below, so the yield the app prints survives someone checking it.

const RENTED_UNITS: any[] = [
  {
    id: 'demo-unit-seagate',
    name: 'Seagate Building 2',
    unitNumber: '1204',
    project: 'Seagate',
    location: 'Mina Rashid, Dubai',
    area: '1412',
    bedrooms: 2,
    floor: 12,
    type: 'Apartment',
    purchaseValue: 3150000,
    currentValue: 3690000,
    monthlyRental: 16250,
    purchaseDate: '2022-02-14',
    constructionDeveloper: 'Emaar Properties',
  },
  {
    id: 'demo-unit-creek-vistas',
    name: 'Creek Vistas Heights',
    unitNumber: '1802',
    project: 'Sobha Hartland',
    location: 'Dubai',
    area: '742',
    bedrooms: 1,
    floor: 18,
    type: 'Apartment',
    purchaseValue: 1320000,
    currentValue: 1685000,
    monthlyRental: 8750,
    purchaseDate: '2021-11-03',
    constructionDeveloper: 'Sobha Realty',
  },
  {
    id: 'demo-unit-wynwood',
    name: 'Wynwood Residences',
    unitNumber: '0711',
    project: 'Jumeirah Village Circle',
    location: 'Dubai',
    area: '1128',
    bedrooms: 2,
    floor: 7,
    type: 'Apartment',
    purchaseValue: 1180000,
    currentValue: 1420000,
    monthlyRental: 7900,
    purchaseDate: '2023-01-19',
    constructionDeveloper: 'Object 1',
  },
  {
    id: 'demo-unit-one-park-central',
    name: 'One Park Central',
    unitNumber: '2205',
    project: 'Jumeirah Village Circle',
    location: 'Dubai',
    area: '810',
    bedrooms: 1,
    floor: 22,
    type: 'Apartment',
    purchaseValue: 1050000,
    currentValue: 1310000,
    monthlyRental: 6600,
    purchaseDate: '2022-08-22',
    constructionDeveloper: 'Iman Developers',
  },
  {
    id: 'demo-unit-address-residences',
    name: 'Address Residences',
    unitNumber: '3306',
    project: 'Dubai Marina',
    location: 'Dubai',
    area: '1905',
    bedrooms: 3,
    floor: 33,
    type: 'Apartment',
    purchaseValue: 4850000,
    currentValue: 6100000,
    monthlyRental: 29000,
    purchaseDate: '2021-06-30',
    constructionDeveloper: 'Emaar Properties',
  },
].map((u) => ({
  ...u,
  investorId: DEMO_INVESTOR_ID,
  status: 'rented',
  occupancyStatus: 'Occupied',
  // Handed over the year after booking — the valuation report prints this,
  // and an em-dash there on a tenanted unit looks like missing data.
  completionDate: `${Number(u.purchaseDate.slice(0, 4)) + 1}-${u.purchaseDate.slice(5)}`,
  isOffplan: false,
  propertyStatus: 'Handed Over',
  constructionCompletionPercent: 100,
  constructionStatus: 'Finished',
  appreciationAmount: u.currentValue - u.purchaseValue,
  appreciationPercentage: Number((((u.currentValue - u.purchaseValue) / u.purchaseValue) * 100).toFixed(1)),
  dataSource: 'demo-snapshot',
}));

// --- The two that were sold --------------------------------------------
// Seller-side costs only: the 2% agency commission, the trustee office fee
// (AED 4,200 over the 500k threshold — see trusteeFeeFor) and the
// developer NOC. The 4% DLD transfer fee is the buyer's in both of these,
// which is the usual split and keeps the net proceeds honest. computeSale
// re-derives the whole return from these fields, so they must add up.

const SOLD_UNITS: any[] = [
  {
    id: 'demo-unit-silena',
    name: 'Silena Residences',
    unitNumber: '0906',
    project: 'Dubai Islands',
    location: 'Dubai',
    area: '1035',
    bedrooms: 2,
    floor: 9,
    type: 'Apartment',
    purchaseValue: 2180000,
    purchaseDate: '2023-03-15',
    salePrice: 2760000,
    saleDate: '2026-06-24',
    saleCosts: { commission: 55200, trusteeFee: 4200, nocFee: 1575 },
    constructionDeveloper: 'Dubai Islands Developments',
  },
  {
    id: 'demo-unit-the-crest',
    name: 'The Crest · Tower C',
    unitNumber: '1407',
    project: 'Sobha Hartland',
    location: 'Dubai',
    area: '698',
    bedrooms: 1,
    floor: 14,
    type: 'Apartment',
    purchaseValue: 1180000,
    purchaseDate: '2021-09-08',
    salePrice: 1640000,
    saleDate: '2026-04-11',
    saleCosts: { commission: 32800, trusteeFee: 4200, nocFee: 1575 },
    constructionDeveloper: 'Sobha Realty',
  },
].map((u) => ({
  ...u,
  investorId: DEMO_INVESTOR_ID,
  status: 'sold',
  // An owner-recorded sale stays editable in the product; a platform-closed
  // one is read-only. Owner-recorded is the one worth showing.
  saleRecordedBy: 'owner',
  saleType: 'transfer',
  soldAt: u.saleDate,
  completionDate: `${Number(u.purchaseDate.slice(0, 4)) + 1}-${u.purchaseDate.slice(5)}`,
  // A sold unit is carried at what it sold for, not at an estimate.
  currentValue: u.salePrice,
  monthlyRental: 0,
  occupancyStatus: 'Sold',
  isOffplan: false,
  propertyStatus: 'Sold',
  constructionCompletionPercent: 100,
  constructionStatus: 'Finished',
  appreciationAmount: u.salePrice - u.purchaseValue,
  appreciationPercentage: Number((((u.salePrice - u.purchaseValue) / u.purchaseValue) * 100).toFixed(1)),
  dataSource: 'demo-snapshot',
}));

// --- The three still being built ---------------------------------------
// No rent on an unbuilt unit — the product shows no yield rather than a
// projected one, which is the honest state and worth filming.

const CONSTRUCTION_UNITS: any[] = [
  {
    id: 'demo-unit-damac-bay',
    name: 'Damac Bay 2 · Tower B',
    unitNumber: '2408',
    project: 'Damac Bay 2',
    location: 'Dubai Harbour, Dubai',
    area: '782',
    bedrooms: 1,
    floor: 24,
    type: 'Apartment',
    purchaseValue: 2050000,
    currentValue: 2340000,
    purchaseDate: '2023-05-08',
    estimatedCompletionDate: '2027-09-30',
    constructionCompletionPercent: 62,
    constructionDeveloper: 'DAMAC Properties',
    constructionStartDate: '2023-11-15',
  },
  {
    id: 'demo-unit-nautica',
    name: 'Nautica Two',
    unitNumber: 'N2-4101',
    project: 'Dubai Maritime City',
    location: 'Dubai',
    area: '651',
    bedrooms: 1,
    floor: 41,
    type: 'Apartment',
    purchaseValue: 1395000,
    currentValue: 1690000,
    purchaseDate: '2024-02-27',
    // A MONTH FROM HANDOVER. Deliberate: this is the moment an off-plan owner
    // actually needs the app — snagging, final payment, keys — so the demo
    // portfolio keeps one unit sitting in it. The handover notification in
    // demoNotifications.ts is built off this same date.
    estimatedCompletionDate: HANDOVER_SOON,
    constructionCompletionPercent: 97,
    constructionDeveloper: 'Select Group',
    constructionStartDate: '2024-06-10',
  },
  // Sobha One, two units in the same development handing over together in
  // December — the case an owner asks about most: "both of mine land in the
  // same month, what do I have to do twice?"
  {
    id: 'demo-unit-sobha-a5003',
    name: 'Sobha One · Tower A',
    unitNumber: 'A-5003',
    project: 'Sobha One',
    location: 'Dubai',
    area: '1150',
    bedrooms: 2,
    floor: 50,
    type: 'Apartment',
    purchaseValue: 2750000,
    currentValue: 3150000,
    purchaseDate: '2024-05-20',
    estimatedCompletionDate: '2026-12-31',
    constructionCompletionPercent: 88,
    constructionDeveloper: 'Sobha Realty',
    constructionStartDate: '2024-09-02',
  },
  {
    id: 'demo-unit-sobha-d1705',
    name: 'Sobha One · Tower D',
    unitNumber: 'D-1705',
    project: 'Sobha One',
    location: 'Dubai',
    area: '782',
    bedrooms: 1,
    floor: 17,
    type: 'Apartment',
    purchaseValue: 1850000,
    currentValue: 2120000,
    purchaseDate: '2024-08-14',
    estimatedCompletionDate: '2026-12-31',
    constructionCompletionPercent: 84,
    constructionDeveloper: 'Sobha Realty',
    constructionStartDate: '2024-11-11',
  },
  {
    id: 'demo-unit-clearpoint',
    name: 'Clearpoint Building 3',
    unitNumber: '520',
    project: 'Rashid Yachts & Marina',
    location: 'Dubai',
    area: '1187',
    bedrooms: 2,
    floor: 5,
    type: 'Apartment',
    purchaseValue: 2420000,
    currentValue: 2735000,
    purchaseDate: '2025-01-16',
    estimatedCompletionDate: '2028-06-30',
    constructionCompletionPercent: 31,
    constructionDeveloper: 'Emaar Properties',
    constructionStartDate: '2025-05-05',
  },
].map((u) => ({
  ...u,
  investorId: DEMO_INVESTOR_ID,
  status: 'active',
  occupancyStatus: 'Offplan',
  isOffplan: true,
  propertyStatus: 'Off-Plan',
  monthlyRental: 0,
  constructionStatus: 'Active',
  appreciationAmount: u.currentValue - u.purchaseValue,
  appreciationPercentage: Number((((u.currentValue - u.purchaseValue) / u.purchaseValue) * 100).toFixed(1)),
  dataSource: 'demo-snapshot',
}));

/**
 * The portfolio in the `Unit` shape InvestorPortal holds in state, for the
 * showcase Owner view (demoMode). InvestorPortal's Unit interface is local to
 * that file, so this is typed loosely and cast at the use site.
 *
 * investorId must match DEMO_INVESTOR.id in demoMode.ts — several screens
 * filter units by it, and a mismatch shows an empty portfolio.
 */
export const DEMO_PORTAL_UNITS: any[] = [
  ...RENTED_UNITS,
  ...CONSTRUCTION_UNITS,
  ...SOLD_UNITS,
];

/**
 * The same ten units in the `Property` shape PortfolioView lists, for the
 * login-free Asset Desk tour. Derived rather than typed out a second time —
 * two hand-maintained copies of one portfolio drift, and the tour and the
 * showcase would then disagree on camera.
 */
export const DEMO_PROPERTIES: Property[] = DEMO_PORTAL_UNITS.map((u) => ({
  id: u.id,
  name: u.name,
  project: u.project,
  location: u.location,
  unitNumber: u.unitNumber,
  area: Number(u.area),
  bedrooms: u.bedrooms,
  type: u.type,
  purchaseValue: u.purchaseValue,
  currentValue: u.currentValue,
  marketValue: u.currentValue,
  monthlyRental: u.monthlyRental,
  purchaseDate: u.purchaseDate,
  estimatedCompletionDate: u.estimatedCompletionDate,
  // The card badge spells it 'Offplan'; the Property shape spells it
  // 'Off-plan'. Same state, two spellings, so translate rather than share.
  occupancyStatus: u.occupancyStatus === 'Offplan' ? 'Off-plan' : u.occupancyStatus,
  developer: u.constructionDeveloper,
  projectProgress: u.constructionCompletionPercent,
  isOffplan: u.isOffplan,
  appreciationAmount: u.appreciationAmount,
  // Sold units carry their sale through to the tour, so realised return and
  // the Sold tab render there too.
  status: u.status,
  salePrice: u.salePrice,
  saleDate: u.saleDate,
  saleCosts: u.saleCosts,
  saleType: u.saleType,
})) as Property[];

// ============================================================
// EVIDENCE BEHIND EACH UNIT
//
// Seagate and Damac Bay above are written by hand because their evidence
// tells a specific story (same-project comps and real Ejari for one; the
// area-tier rent fallback for an unbuilt tower for the other).
//
// The remaining eight are BUILT from the unit itself, so the arithmetic
// cannot drift when someone edits a price: psf = value ÷ size, the four
// comparables bracket that psf, each comp's amount is its own size × psf,
// and the yield is the rent evidence over the same value. Change a unit's
// currentValue and its whole evidence pack moves with it.
// ============================================================

const round = (n: number) => Math.round(n);
/** Transaction amounts land on round thousands, the way registered ones do. */
const toThousands = (n: number) => Math.round(n / 1000) * 1000;

type EvidenceSpec = {
  /** DLD area the comps are drawn from. */
  areaName: string;
  confidence: 'high' | 'medium' | 'low';
  /** Rent evidence behind the yield line; omit for an unbuilt unit. */
  annualRent?: number;
  /** 'project' when the building itself has Ejari history, 'area' when not. */
  rentMatch?: 'project' | 'area';
  /** Median AED/sqft of the surrounding area, for the benchmark strip. */
  areaPsf: number;
  /** 6-month move in the project's median psf, in %. */
  change6m: number;
  /** Registered sales in the building over the window. */
  buildingSales12m: number;
  /** Service charge AED/sqft, where there is an invoice on record. */
  serviceChargePsf?: number;
};

/** Four comparables that bracket the estimate, two under and two over. */
const COMP_PSF_DELTAS = [1.4, -1.7, 2.1, -0.9];
const COMP_SIZE_DELTAS = [-0.6, 1.8, 0.9, -1.4];
const COMP_DATES = ['2026-07-02', '2026-05-27', '2026-04-09', '2026-03-21'];
const RENT_DATES = ['2026-06-20', '2026-05-02', '2026-03-12', '2026-01-22'];
const RENT_DELTAS = [3.5, -4.2, 6.1, -2.4];
const TREND_MONTHS = ['2026-02', '2026-03', '2026-04', '2026-05', '2026-06', '2026-07'];

function buildDemoDld(u: any, x: EvidenceSpec) {
  const size = Number(u.area);
  const psf = round(u.currentValue / size);
  const rooms = `${u.bedrooms} B/R`;

  const comps = COMP_PSF_DELTAS.map((d, i) => {
    const sizeSqft = round(size * (1 + COMP_SIZE_DELTAS[i] / 100));
    const pricePerSqft = round(psf * (1 + d / 100));
    return {
      transactionId: `DLD-2026-4${(u.id.length * 37 + i * 8419 + 100000).toString().slice(0, 6)}`,
      date: COMP_DATES[i],
      project: u.name,
      rooms,
      sizeSqft,
      amount: toThousands(sizeSqft * pricePerSqft),
      pricePerSqft,
      isOffplan: !!u.isOffplan,
      sizeDeltaPct: COMP_SIZE_DELTAS[i],
    };
  });

  const rentals = x.annualRent
    ? RENT_DELTAS.map((d, i) => {
        const annualRent = toThousands(x.annualRent! * (1 + d / 100));
        return {
          startDate: RENT_DATES[i],
          annualRent,
          monthlyRent: round(annualRent / 12),
          sizeSqft: comps[i].sizeSqft,
          rooms,
          isRenewal: i % 2 === 1,
          project: x.rentMatch === 'area' ? x.areaName : u.project,
        };
      })
    : [];

  // The trend walks up to today's psf rather than away from it, so the last
  // point on the chart is the number the rest of the report quotes.
  const start = psf / (1 + x.change6m / 100);
  const wobble = [0, 0.9, 1.4, 1.1, 2.2, 0];
  const series = TREND_MONTHS.map((month, i) => ({
    month,
    pricePerSqft:
      i === TREND_MONTHS.length - 1
        ? psf
        : round((start + ((psf - start) * i) / (TREND_MONTHS.length - 1)) * (1 + wobble[i] / 100)),
  }));

  return {
    unitId: u.id,
    unitName: `${u.name} · ${u.unitNumber}`,
    project: u.project,
    area: x.areaName,

    dldMarketValue: u.currentValue,
    dldMarketValueLow: toThousands(u.currentValue * 0.955),
    dldMarketValueHigh: toThousands(u.currentValue * 1.045),
    dldPricePerSqft: psf,
    valuationConfidence: x.confidence,
    estimateConfidence: x.confidence,
    comparablesCount: comps.length,
    valuationCompsUsed: comps.length,
    strictCompsCount: comps.length,
    valuationDedupDropped: 0,
    valuationWindowMonths: 12,
    roiReliable: !u.isOffplan,
    matchType: 'project',
    matchArea: x.areaName,
    marketValuation: {
      source_label: `${u.name} · ${rooms}${u.isOffplan ? ' off-plan' : ''} · median AED/sqft, last 12 months`,
    },

    purchaseValue: u.purchaseValue,
    currentValue: u.currentValue,
    unrealizedGain: u.currentValue - u.purchaseValue,
    marketAppreciation: u.appreciationPercentage,

    dldAvgAnnualRent: x.annualRent || 0,
    dldAvgMonthlyRent: x.annualRent ? round(x.annualRent / 12) : 0,
    dldRentalYield: x.annualRent ? Number(((x.annualRent / u.currentValue) * 100).toFixed(2)) : 0,
    rentalComparablesCount: rentals.length,
    rentalMatchType: x.rentMatch || 'project',

    priceTrend: { change6m: x.change6m, series },

    estimateComparables: comps,
    buildingSales: [],
    recentComparables: comps.map((c) => ({
      date: c.date,
      amount: c.amount,
      sizeSqft: c.sizeSqft,
      pricePerSqft: c.pricePerSqft,
      rooms: c.rooms,
      type: 'Apartment',
      isOffplan: c.isOffplan ? 'Yes' : 'No',
      project: c.project,
    })),
    recentRentals: rentals,
    lastSync: SNAPSHOT_AT,
  };
}

function buildDemoBenchmark(u: any, x: EvidenceSpec) {
  const size = Number(u.area);
  const psf = round(u.currentValue / size);
  const buildingPsf = round(psf * 0.978);
  const serviceChargeAnnual = x.serviceChargePsf ? round(x.serviceChargePsf * size) : null;
  const grossYieldPct = x.annualRent
    ? Number(((x.annualRent / u.currentValue) * 100).toFixed(2))
    : null;

  return {
    available: true,
    windowMonths: 12,
    minComps: 3,
    subject: {
      name: u.name,
      project: u.project,
      areaName: x.areaName,
      bedrooms: u.bedrooms,
      sizeSqft: size,
      offplan: !!u.isOffplan,
      pricePerSqft: psf,
      pricePerSqftSource: 'dld_valuation',
    },
    building: {
      medianPsf: buildingPsf,
      p25Psf: round(buildingPsf * 0.972),
      p75Psf: round(buildingPsf * 1.031),
      comps: x.buildingSales12m,
      basis: 'bedroom_matched',
      sufficient: x.buildingSales12m >= 3,
      label: u.name,
      vsUnitPct: Number(((psf / buildingPsf - 1) * 100).toFixed(1)),
    },
    area: {
      medianPsf: x.areaPsf,
      p25Psf: round(x.areaPsf * 0.93),
      p75Psf: round(x.areaPsf * 1.08),
      comps: x.buildingSales12m * 3 + 7,
      basis: 'all_unit_types',
      sufficient: true,
      label: x.areaName,
      vsUnitPct: Number(((psf / x.areaPsf - 1) * 100).toFixed(1)),
    },
    trend: {
      building: { changePct: Number((x.change6m * 0.7).toFixed(1)), recentComps: 5, priorComps: 4 },
      area: { changePct: Number((x.change6m * 0.5).toFixed(1)), recentComps: 14, priorComps: 11 },
    },
    liquidity: {
      buildingSales12m: x.buildingSales12m,
      buildingPerQuarter: Number((x.buildingSales12m / 4).toFixed(1)),
      areaSales12m: x.buildingSales12m * 3 + 7,
      areaPerQuarter: Number(((x.buildingSales12m * 3 + 7) / 4).toFixed(1)),
      windowMonths: 12,
    },
    economics: x.annualRent
      ? {
          available: true,
          annualRent: x.annualRent,
          value: u.currentValue,
          grossYieldPct,
          netYieldPct: serviceChargeAnnual
            ? Number((((x.annualRent - serviceChargeAnnual) / u.currentValue) * 100).toFixed(2))
            : null,
          serviceChargePsf: x.serviceChargePsf || null,
          serviceChargeAnnual,
          serviceChargeSource: x.serviceChargePsf ? 'invoice' : null,
          valueBasis: 'dld_valuation',
        }
      : // Unbuilt: no Ejari evidence for the building, so no yield at all
        // rather than a projected one.
        { available: false, reason: 'no_rent_evidence' },
    areaRent: {
      medianRentPsf: x.annualRent ? round(x.annualRent / size) : round(x.areaPsf / 18),
      contracts: x.buildingSales12m * 2 + 11,
      basis: 'bedroom_matched',
      sufficient: true,
    },
    computedAt: SNAPSHOT_AT,
  };
}

/** Evidence knobs per unit. Seagate and Damac Bay are hand-written above. */
const EVIDENCE: Record<string, EvidenceSpec> = {
  'demo-unit-creek-vistas': {
    areaName: 'Sobha Hartland', confidence: 'high', annualRent: 105000, rentMatch: 'project',
    areaPsf: 2145, change6m: 5.4, buildingSales12m: 11, serviceChargePsf: 16.2,
  },
  'demo-unit-wynwood': {
    areaName: 'Jumeirah Village Circle', confidence: 'high', annualRent: 94800, rentMatch: 'project',
    areaPsf: 1288, change6m: 6.2, buildingSales12m: 14, serviceChargePsf: 12.4,
  },
  'demo-unit-one-park-central': {
    areaName: 'Jumeirah Village Circle', confidence: 'medium', annualRent: 79200, rentMatch: 'project',
    areaPsf: 1288, change6m: 4.9, buildingSales12m: 8, serviceChargePsf: 13.1,
  },
  'demo-unit-address-residences': {
    areaName: 'Dubai Marina', confidence: 'high', annualRent: 348000, rentMatch: 'project',
    areaPsf: 2980, change6m: 3.7, buildingSales12m: 12, serviceChargePsf: 21.5,
  },
  'demo-unit-silena': {
    areaName: 'Dubai Islands', confidence: 'medium', annualRent: 150000, rentMatch: 'area',
    areaPsf: 2358, change6m: 5.6, buildingSales12m: 4,
  },
  'demo-unit-the-crest': {
    areaName: 'Sobha Hartland', confidence: 'high', annualRent: 96000, rentMatch: 'project',
    areaPsf: 2145, change6m: 4.4, buildingSales12m: 9, serviceChargePsf: 15.8,
  },
  'demo-unit-nautica': {
    areaName: 'Dubai Maritime City', confidence: 'medium', areaPsf: 2410,
    change6m: 4.1, buildingSales12m: 6,
  },
  'demo-unit-sobha-a5003': {
    areaName: 'Sobha Hartland', confidence: 'high', areaPsf: 2145,
    change6m: 4.8, buildingSales12m: 13,
  },
  'demo-unit-sobha-d1705': {
    areaName: 'Sobha Hartland', confidence: 'high', areaPsf: 2145,
    change6m: 4.8, buildingSales12m: 13,
  },
  'demo-unit-clearpoint': {
    areaName: 'Rashid Yachts & Marina', confidence: 'medium', areaPsf: 2255,
    change6m: 3.4, buildingSales12m: 5,
  },
};

const byId = (id: string) => DEMO_PORTAL_UNITS.find((u) => u.id === id);
const BUILT_DLD = Object.entries(EVIDENCE).map(([id, spec]) => buildDemoDld(byId(id), spec));

// ------------------------------------------------------------
// Construction timelines. The three unbuilt units are the point — that
// screen is where an off-plan owner actually lives — but the finished ones
// carry a completed timeline too, so tapping one never dead-ends.
// ------------------------------------------------------------
function buildDemoTimeline(u: any) {
  const done = u.constructionCompletionPercent >= 100;
  const started = u.constructionStartDate || `${Number(u.purchaseDate.slice(0, 4)) + 1}-03-01`;
  const completion = u.estimatedCompletionDate || `${Number(u.purchaseDate.slice(0, 4)) + 3}-12-31`;
  return {
    projectName: u.name,
    projectNumber: String(1200 + u.name.length * 31),
    developerName: u.constructionDeveloper,
    status: done ? 'Finished' : 'Active',
    completionPercentage: u.constructionCompletionPercent,
    totalUnits: 280 + u.name.length * 17,
    totalArea: 48000 + u.name.length * 900,
    escrowAccount: 'Escrow · Dubai Islamic Bank',
    milestones: [
      { label: 'Booking Started', date: u.purchaseDate, completed: true, order: 1 },
      { label: 'Construction Started', date: started, completed: true, order: 2 },
      { label: 'Construction Progress', percentage: u.constructionCompletionPercent, completed: done, order: 3 },
      { label: done ? 'Completed' : 'Expected Completion', date: completion, completed: done, order: 4 },
    ],
    rawData: { registrationDate: u.purchaseDate, startDate: started, completionDate: completion },
  };
}

export const DEMO_TIMELINES: Record<string, any> = {
  'demo-unit-seagate': {
    projectName: 'Seagate', projectNumber: '1938', developerName: 'Emaar Properties',
    status: 'Finished', completionPercentage: 100, totalUnits: 618, totalArea: 74300,
    escrowAccount: 'Escrow · Mashreq Bank',
    milestones: [
      { label: 'Booking Started', date: '2022-02-14', completed: true, order: 1 },
      { label: 'Construction Started', date: '2022-09-01', completed: true, order: 2 },
      { label: 'Construction Progress', percentage: 100, completed: true, order: 3 },
      { label: 'Expected Completion', date: '2025-12-31', completed: true, order: 4 },
    ],
    rawData: { registrationDate: '2022-01-20', startDate: '2022-09-01', completionDate: '2025-12-31' },
  },
  'demo-unit-damac-bay': {
    projectName: 'Damac Bay 2', projectNumber: '2264', developerName: 'DAMAC Properties',
    status: 'Active', completionPercentage: 62, totalUnits: 1104, totalArea: 96800,
    escrowAccount: 'Escrow · Dubai Islamic Bank',
    milestones: [
      { label: 'Booking Started', date: '2023-05-08', completed: true, order: 1 },
      { label: 'Construction Started', date: '2023-11-15', completed: true, order: 2 },
      { label: 'Construction Progress', percentage: 62, completed: false, order: 3 },
      { label: 'Expected Completion', date: '2027-09-30', completed: false, order: 4 },
    ],
    rawData: { registrationDate: '2023-04-02', startDate: '2023-11-15', completionDate: '2027-09-30' },
  },
  ...Object.fromEntries(
    Object.keys(EVIDENCE).map((id) => [id, buildDemoTimeline(byId(id))]),
  ),
};

export const DEMO_BENCHMARKS: Record<string, any> = {
  'demo-unit-seagate': {
    available: true,
    windowMonths: 12,
    minComps: 3,
    subject: {
      name: 'Seagate Building 2', project: 'Seagate', areaName: 'Mina Rashid',
      bedrooms: 2, sizeSqft: 1412, offplan: false,
      pricePerSqft: 2613, pricePerSqftSource: 'dld_valuation',
    },
    building: { medianPsf: 2548, p25Psf: 2491, p75Psf: 2624, comps: 9, basis: 'bedroom_matched', sufficient: true, label: 'Seagate', vsUnitPct: 2.6 },
    area: { medianPsf: 2415, p25Psf: 2260, p75Psf: 2588, comps: 34, basis: 'all_unit_types', sufficient: true, label: 'Mina Rashid', vsUnitPct: 8.2 },
    trend: {
      building: { changePct: 3.1, recentComps: 5, priorComps: 4 },
      area: { changePct: 2.2, recentComps: 18, priorComps: 15 },
    },
    liquidity: { buildingSales12m: 9, buildingPerQuarter: 2.3, areaSales12m: 34, areaPerQuarter: 8.5, windowMonths: 12 },
    // Rent 198,000 on a value of 3,690,000 = 5.37% gross. Service charge
    // 14.50/sqft × 1,412 sqft = 20,474/yr, so net = (198,000 − 20,474) / 3,690,000
    // = 4.81%. The arithmetic has to survive an investor checking it.
    economics: {
      available: true, annualRent: 198000, value: 3690000,
      grossYieldPct: 5.37, netYieldPct: 4.81,
      serviceChargePsf: 14.5, serviceChargeAnnual: 20474, serviceChargeSource: 'invoice',
      valueBasis: 'dld_valuation',
    },
    areaRent: { medianRentPsf: 132, contracts: 41, basis: 'bedroom_matched', sufficient: true },
    computedAt: SNAPSHOT_AT,
  },
  'demo-unit-damac-bay': {
    available: true,
    windowMonths: 12,
    minComps: 3,
    subject: {
      name: 'Damac Bay 2 · Tower B', project: 'Damac Bay 2', areaName: 'Dubai Harbour',
      bedrooms: 1, sizeSqft: 782, offplan: true,
      pricePerSqft: 2992, pricePerSqftSource: 'dld_valuation',
    },
    building: { medianPsf: 2946, p25Psf: 2901, p75Psf: 3031, comps: 7, basis: 'bedroom_matched', sufficient: true, label: 'Damac Bay 2', vsUnitPct: 1.6 },
    area: { medianPsf: 3105, p25Psf: 2880, p75Psf: 3390, comps: 22, basis: 'all_unit_types', sufficient: true, label: 'Dubai Harbour', vsUnitPct: -3.6 },
    trend: {
      building: { changePct: 1.4, recentComps: 4, priorComps: 3 },
      area: null,   // too few in one of the two windows — chip simply hides
    },
    liquidity: { buildingSales12m: 7, buildingPerQuarter: 1.8, areaSales12m: 22, areaPerQuarter: 5.5, windowMonths: 12 },
    // Unbuilt: no Ejari evidence for the building, so no yield at all rather
    // than a projected one. This is the honest state for off-plan.
    economics: { available: false, reason: 'no_rent_evidence' },
    areaRent: { medianRentPsf: 185, contracts: 27, basis: 'bedroom_matched', sufficient: true },
    computedAt: SNAPSHOT_AT,
  },
  ...Object.fromEntries(
    Object.entries(EVIDENCE).map(([id, spec]) => [id, buildDemoBenchmark(byId(id), spec)]),
  ),
};

/**
 * Same shape as the /dld/market-intelligence response, passed to the real
 * PortfolioView as `dldDataOverride` so it renders without fetching.
 *
 * Portfolio totals cover the units still held: a sold unit is a realised
 * return, not part of what the portfolio is worth today — the same rule
 * portfolioMetrics applies (it filters isSold out of the holdings).
 */
const HELD = DEMO_PORTAL_UNITS.filter((u) => u.status !== 'sold');

export const DEMO_DLD_DATA = {
  portfolio: {
    totalUnits: HELD.length,
    totalPurchaseValue: HELD.reduce((s, u) => s + (u.purchaseValue || 0), 0),
    totalMarketValue: HELD.reduce((s, u) => s + (u.currentValue || 0), 0),
    totalUnrealizedGain: HELD.reduce((s, u) => s + ((u.currentValue || 0) - (u.purchaseValue || 0)), 0),
  },
  units: [SEAGATE_DLD, DAMAC_BAY_DLD, ...BUILT_DLD],
  lastSync: SNAPSHOT_AT,
  syncStatus: 'demo-snapshot',
  dataSource: 'demo-snapshot',
};

// ------------------------------------------------------------
// Sample Dubai Sell inventory, for the Calculator tab's BUY side.
//
// The live version of this list is fetched from
// GET /api/sell-platform/listings — a public endpoint, but the tour is
// held to "no network call from this subtree" (see the SAFETY note
// above), so it is handed these instead via `listingsOverride`.
//
// FICTIONAL units in real communities, priced so the discount and the
// resale arithmetic on screen stay plausible: marketValue > askingPrice,
// and discountPercent = (market − asking) / market, the same formula the
// backend applies. No photoUrl — the tour carries no image assets, and
// the card renders without one.
// ------------------------------------------------------------
export const DEMO_SELL_LISTINGS: SellListing[] = [
  {
    id: 'demo-listing-marina',
    title: 'Marina Gate — high floor, marina view',
    propertyType: 'Apartment',
    location: 'Dubai Marina',
    sizeSqft: 1180,
    bedrooms: 2,
    askingPrice: 2450000,
    marketValue: 2900000,
    isDistress: true,
    discountPercent: 16,
    description: 'Owner relocating, sale to complete within eight weeks.',
    photoUrl: null,
  },
  {
    id: 'demo-listing-mina-rashid',
    title: 'Sirdhana at Mina Rashid — waterfront',
    propertyType: 'Apartment',
    location: 'Mina Rashid, Dubai',
    sizeSqft: 1395,
    bedrooms: 2,
    askingPrice: 2980000,
    marketValue: 3420000,
    isDistress: true,
    discountPercent: 13,
    description: 'Bank-instructed sale, vacant on transfer.',
    photoUrl: null,
  },
  {
    id: 'demo-listing-business-bay',
    title: 'Business Bay — studio, rented at 6.4%',
    propertyType: 'Apartment',
    location: 'Business Bay',
    sizeSqft: 520,
    bedrooms: 1,
    askingPrice: 890000,
    marketValue: 1010000,
    isDistress: true,
    discountPercent: 12,
    description: 'Tenanted until 2027, seller needs a quick exit.',
    photoUrl: null,
  },
];

// ============================================================
// PAYMENT PLANS — what the owner has actually paid out, and when.
//
// The report's "payments per year" table reads installment plans from
// /api/installment-plans, which a demo session cannot reach, so that whole
// section vanished — and for an off-plan portfolio the payment schedule IS
// the story: what has gone out, what is still committed, and in which year.
//
// Each plan is generated from its unit so the numbers tie back: the
// milestones always sum to that unit's purchase price, a handed-over unit is
// fully paid, and an off-plan unit is paid up to roughly its construction
// percentage with the rest still ahead of it.
// ============================================================

const PLAN_SHAPE = [
  { pct: 20, label: 'Down payment' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 10, label: 'Construction milestone' },
  { pct: 20, label: 'On handover' },
];

function buildPlan(u: any) {
  const start = new Date(u.purchaseDate);
  const endIso = u.estimatedCompletionDate || u.completionDate || u.purchaseDate;
  const end = new Date(endIso);
  const spanMonths = Math.max(6, Math.round((end.getTime() - start.getTime()) / (30.44 * 24 * 3600 * 1000)));
  const step = spanMonths / PLAN_SHAPE.length;
  const today = Date.now();

  // A handed-over unit is paid in full; an off-plan one is paid up to where
  // the build has got to, which is what the developer has been able to invoice.
  const paidThroughPct = u.isOffplan ? u.constructionCompletionPercent : 100;
  let cumulative = 0;

  const milestones = PLAN_SHAPE.map((m, i) => {
    const due = new Date(start);
    due.setMonth(due.getMonth() + Math.round(step * (i + 1)));
    const dueIso = due.toISOString().slice(0, 10);
    cumulative += m.pct;
    const paid = cumulative <= paidThroughPct && due.getTime() < today;
    return {
      installmentNumber: i + 1,
      description: i === 0 ? m.label : `${m.label} ${cumulative}%`,
      percentage: m.pct,
      amount: Math.round((u.purchaseValue * m.pct) / 100),
      dueDate: dueIso,
      status: paid ? 'paid' : due.getTime() < today ? 'overdue' : 'pending',
      paidDate: paid ? dueIso : null,
    };
  });

  return {
    id: `plan-${u.id}`,
    unitId: u.id,
    unitName: u.name,
    project: u.project,
    unitNumber: u.unitNumber,
    totalPurchasePrice: u.purchaseValue,
    milestones,
  };
}

/** Held units only — a sold unit's instalments are somebody else's now. */
export const DEMO_PLANS: any[] = DEMO_PORTAL_UNITS
  .filter((u) => u.status !== 'sold')
  .map(buildPlan);

/**
 * Sample /api/dld/deal-history payload for the InvestMenter Brain in demo and
 * film mode. SAMPLE FIGURES, like everything in this file — the live page reads
 * the anonymised aggregate from the backend.
 */
export const DEMO_DEAL_HISTORY = {
  generatedAt: '2026-09-29T08:00:00.000Z',
  minClients: 3,
  totals: {
    deals: 214, clients: 131, projects: 38, volume: 486500000,
    firstDeal: '2019-03-11', lastDeal: '2026-09-02', resales: 27, medianAnnualisedRoi: 11,
  },
  projects: [
    { key: 'sobha hartland', project: 'Sobha Hartland', deals: 22, clients: 17, medianPsf: 2150, psfSample: 22, firstBought: '2020-02-14', lastBought: '2026-07-30', resales: 5, medianAnnualisedRoi: 13 },
    { key: 'damac bay 2', project: 'Damac Bay 2', deals: 18, clients: 14, medianPsf: 3000, psfSample: 18, firstBought: '2023-01-09', lastBought: '2026-08-21', resales: 0, medianAnnualisedRoi: null },
    { key: 'seagate', project: 'Seagate', deals: 15, clients: 11, medianPsf: 2450, psfSample: 15, firstBought: '2022-05-03', lastBought: '2026-06-12', resales: 3, medianAnnualisedRoi: 9 },
    { key: 'jumeirah village circle', project: 'Jumeirah Village Circle', deals: 13, clients: 12, medianPsf: 1250, psfSample: 13, firstBought: '2019-03-11', lastBought: '2026-05-19', resales: 6, medianAnnualisedRoi: 10 },
    { key: 'dubai islands', project: 'Dubai Islands', deals: 9, clients: 8, medianPsf: 2600, psfSample: 9, firstBought: '2024-02-01', lastBought: '2026-09-02', resales: 0, medianAnnualisedRoi: null },
    { key: 'dubai marina', project: 'Dubai Marina', deals: 7, clients: 6, medianPsf: 2300, psfSample: 7, firstBought: '2019-10-22', lastBought: '2025-11-30', resales: 4, medianAnnualisedRoi: 8 },
  ],
  hiddenProjects: 21,
  yours: [
    { key: 'seagate', project: 'Seagate', yourUnits: 1, yourPsf: 2350, clientsPsf: 2450, clientDeals: 15, clients: 11, diffPct: -4.1 },
    { key: 'damac bay 2', project: 'Damac Bay 2', yourUnits: 1, yourPsf: 2950, clientsPsf: 3000, clientDeals: 18, clients: 14, diffPct: -1.7 },
  ],
};
